import React, { useEffect } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useThemeStore } from '../../store/themeStore';
import { ThemeSetting } from '../../lib/theme';

const OPTIONS: { value: ThemeSetting; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Monitor },
];

/**
 * Light / dark / system switch. Rendered wherever the user might be — the
 * header when signed in, the login and public status pages when not — so the
 * appearance is never stuck on a page they cannot get past.
 */
export function ThemeToggle() {
  const setting = useThemeStore(s => s.setting);
  const setTheme = useThemeStore(s => s.setTheme);
  const syncWithSystem = useThemeStore(s => s.syncWithSystem);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    // Follow the OS while it is switching, e.g. at sunset on a schedule.
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => syncWithSystem();
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [syncWithSystem]);

  return (
    <div className="theme-toggle" role="radiogroup" aria-label="Colour theme">
      {OPTIONS.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={setting === value}
          aria-label={label}
          title={`${label} theme`}
          className={`theme-toggle-option${setting === value ? ' is-selected' : ''}`}
          onClick={() => setTheme(value)}
        >
          <Icon size={15} aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
